import type { ConsoleThreadListQuery, ThreadDetail } from '@central/api';
import { infiniteQueryOptions } from '@tanstack/react-query';
import { keys, useApi } from '../api';
import { useIsReseller } from './me';

/**
 * The thread routes the signed-in user works through: a reseller's are its
 * own customers' threads under /partner, Rethink's are every thread.
 */
export function useThreadsApi() {
  const api = useApi();
  return useIsReseller() ? api.partner.threads : api.threads;
}

/** The pages of one thread list, shared by the inbox and a ticket page's previous and next. */
export function threadListOptions(
  threads: ReturnType<typeof useThreadsApi>,
  q: ConsoleThreadListQuery,
) {
  return infiniteQueryOptions({
    queryKey: keys.threadList(q),
    queryFn: ({ pageParam }) => threads.list({ ...q, cursor: pageParam }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor,
  });
}

/** A thread's pages, newest page first; the next page is the one before the last loaded. */
export function threadDetailOptions(threads: ReturnType<typeof useThreadsApi>, id: string) {
  return infiniteQueryOptions({
    queryKey: keys.thread(id),
    queryFn: ({ pageParam }) => threads.get(id, pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (page: ThreadDetail) => page.earlierCursor,
  });
}
